import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  AbstractControl,
  Validators,
} from '@angular/forms';

import { defer, Observable } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';

import { BaseClass } from 'src/app/core/base-class';
import { EagerMaxLengthErrorStateMatcher } from 'src/app/components/form-field/error-state-matcher';
import {
  EmployeeMessageLimitService,
  employeeMessageLimitValidator,
} from 'src/app/services/employee-message-limit.service';
import {
  Category,
  LoadedReprovation,
  PreselectionOutcome,
  RefusalPreselection,
  SubmissionReprovation,
} from 'src/app/shared/model/reprovation';
import {
  ResponseSubReason,
  ResponseReason,
} from 'src/app/shared/model/refusal-reasons.model';
import { Reason } from 'src/app/shared/model/refusal-reasons.model';
import { RefusalReasonService } from 'src/app/services/refusal-reason.service';

@Component({
  selector: 'app-reprovation',
  templateUrl: './reprovation.component.html',
  styleUrls: ['./reprovation.component.scss'],
})
export class RefusalComponent extends BaseClass implements OnInit {
  @Output() setReprovalData = new EventEmitter<SubmissionReprovation>();
  /**
   * What became of an offered preselection. The caller knows what it offered;
   * only this component knows what the live catalogue did with it, and
   * `expandedCategoryName` is matched by **label**, which an operator can
   * rename. Four outcomes rather than a boolean, because a boolean made a
   * rename, a reason that moved out of its category and a failed catalogue
   * fetch report identically -- and the renames are what this is here to catch.
   * Emitted once per dialog.
   */
  @Output() preselectionApplied = new EventEmitter<PreselectionOutcome>();

  /**
   * Dialog only: bound alongside `reprovation` it would emit into the parent form
   * and mark it dirty.
   */
  @Input() preselection: RefusalPreselection | null = null;

  form: UntypedFormGroup;
  categories: Category[] = [];
  forceTouch = false;
  readonly messageErrorMatcher = new EagerMaxLengthErrorStateMatcher();
  readonly messageLimit = this.employeeMessageLimit.limit;
  loaderSearch = false;
  refusalReasons: Reason[] = [];
  expandedCategoryId: number | null = null;
  private preselectionReported = false;
  private failureRethrown = false;

  reasonIds$!: Observable<number[]>;

  private _disabled = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private refusalReasonService: RefusalReasonService,
    private employeeMessageLimit: EmployeeMessageLimitService
  ) {
    super();

    this.form = this.formBuilder.group({
      reasons: [[]],
      reason: [{ value: null, disabled: true }],
      message: [
        { value: '', disabled: true },
        employeeMessageLimitValidator(this.messageLimit),
      ],
    });
  }

  get reasons() {
    return this.form.get('reasons') as AbstractControl;
  }

  get reason() {
    return this.form.get('reason') as AbstractControl;
  }

  get message() {
    return this.form.get('message') as AbstractControl;
  }

  get disabled(): boolean {
    return this._disabled;
  }
  @Input() set disabled(disabled: boolean) {
    this._disabled = disabled;

    if (this._disabled) this.message.disable();
    else this.enableMessage();

    this.getCategoriesList();

    if (!this._disabled) {
      this.reasons.updateValueAndValidity();
    }
  }

  @Input() set reprovation({
    reasons,
    label = '',
    message,
  }: LoadedReprovation) {
    if (!this.message.value) {
      this.reason.setValue({ id: '', label }, { emitEvent: false });
      this.message.setValue(message, { emitEvent: false });
    }

    this.reasons.setValue(reasons, { emitEvent: false });

    // A saved message past the limit has to be shortened before the document
    // saves again, so it stays editable on a block that is otherwise read-only.
    // A disabled control is not validated, and the host's own copy of this
    // control is not rendered while the block is closed.
    if (
      this._disabled &&
      this.messageLimit !== null &&
      (this.message.value?.length ?? 0) > this.messageLimit
    ) {
      this.message.enable({ emitEvent: false });
    }
  }

  @Input() set saved(clickedSave: boolean) {
    this.forceTouch = clickedSave;

    if (
      !this._disabled &&
      this.reasons.value?.length &&
      this.reason.value?.id?.length &&
      clickedSave
    ) {
      this.setFormTouched();
    }
  }

  ngOnInit(): void {
    this.setFormObservables();

    this.getCategoriesList();
    // Retry: the `disabled` setter may have loaded the catalogue before this ran.
    this.applyPreselection();
  }

  setFormObservables() {
    // Read at subscribe time: the child that fetches the templates subscribes after
    // this, so a selection already made would otherwise be lost.
    this.reasonIds$ = defer(() => {
      const changes = this.reasons.valueChanges;

      return this.reasons.value?.length
        ? changes.pipe(startWith(this.reasons.value))
        : changes;
    }).pipe(map((reasons: Category[]) => (reasons || []).map(({ id }) => id)));

    this.reasons.valueChanges.subscribe((value) => {
      const hasChosenReasons = value.length > 0;

      // The picker is enabled without emitting: `enable()` would emit whatever
      // it holds, and the subscription below answers by writing that value's
      // text -- nothing, for the "Mensagem personalizada" placeholder a saved
      // refusal seeds -- over the message. That is how Editar on the edit
      // form's closed block wiped the saved message (DEV-2002), and how a
      // typed message was once lost when a reason was added. The message is
      // enabled here instead, since that answer no longer runs. Both branches
      // only on the transition: `disable()` emits too, whatever the current
      // state, so calling it on an already-disabled control cost one redundant
      // `setReprovalData`. Not worth a spec: this component emits several
      // times per `reasonsChanged` anyway, so the one extra is not isolable
      // without asserting a brittle count.
      if (hasChosenReasons) {
        if (this.reason.disabled) {
          this.reason.enable({ emitEvent: false });
          this.enableMessage();
        }
      } else if (this.reason.enabled) this.reason.disable();
    });

    this.form.valueChanges.subscribe(() =>
      this.setReprovalData.emit({
        reasons_ids:
          this.reasons.value?.map(({ id }: { id: number }) => id) || [],
        message: this.message.value,
      })
    );

    this.reason.valueChanges.subscribe((reason) => {
      this.message.setValue(reason?.message ?? '');
      this.enableMessage();
    });
  }

  reasonsChanged(reasons: Category[]): void {
    this.reasons.setValue(reasons);

    if (!reasons?.length) {
      this.reason.setValue(null);
      this.message.setValue('');
      this.message.disable();
    }
  }

  private setFormTouched(): void {
    // `add`, not `set`: the length limit is on the control from construction.
    this.message.addValidators(Validators.required);
    this.enableMessage();
    this.form.markAllAsTouched();
  }

  private getCategoriesList(): void {
    if (!this.disabled && !this.categories?.length) {
      this.refusalReasonService
        .getCategories()
        .pipe(
          map((reasons: ResponseReason[]) => this.buildReasons(reasons)),
          takeUntil(this.unsubscribe)
        )
        .subscribe({
          next: (categories: Category[]) => {
            this.categories = categories;
            this.applyPreselection();
          },
          // The dialog opens with no categories at all, which is a worse
          // problem than the telemetry. Reported apart from `unresolved` so a
          // catalogue outage is not read as an operator renaming
          // `Foto/Arquivo`.
          //
          // Rethrown, and that is the load-bearing line: with only a `next`
          // handler this error reached `reportUnhandledError` and from there
          // Angular's `ErrorHandler`, which is `SentryErrorHandler`. Handling it
          // here consumes it, so adding this callback without the throw would
          // have silenced the very failure the paragraph above calls worse than
          // the telemetry. Throwing re-enters `reportUnhandledError`, so the
          // global handler still sees it and nothing in between absorbs it --
          // `ApiService.get` is a bare `http.get`, and `getCategories` rethrows
          // after clearing its cache.
          error: (error: unknown) => {
            if (this.preselection) this.report('unavailable');

            // Once, like the report above. `getCategoriesList` runs from the
            // `disabled` setter as well as `ngOnInit`, and its guard cannot see
            // a request already in flight -- `getCategories` shares the one HTTP
            // call, so both subscribers get the same failure, and on the edit
            // form (the only consumer that binds `[disabled]`) that turned one
            // outage into two identical Sentry events.
            if (this.failureRethrown) return;

            this.failureRethrown = true;

            throw error;
          },
        });
    }
  }

  /**
   * Through `reasonsChanged` because that is the only path that enables the
   * template picker and the message.
   */
  private applyPreselection(): void {
    // `reasonIds$` means setFormObservables ran; ngOnInit retries after it.
    if (!this.reasonIds$ || !this.preselection || this.reasons.value?.length) {
      return;
    }

    // Nothing to resolve against yet. This runs once from `ngOnInit` and again
    // when the catalogue lands, and reporting "did not resolve" against an
    // empty catalogue would report the wrong answer for the attempt that
    // matters.
    if (!this.categories.length) return;

    const { reasonIds, expandedCategoryName } = this.preselection;
    const category = this.categoryByName(expandedCategoryName);
    // Inside the named category only: an id that moved out of it is not the problem
    // the caller meant.
    const selected = (category?.children || []).filter(({ id }: Category) =>
      reasonIds.includes(id)
    );

    // Only when nothing resolved: expanding would block `subCategoriesSelected`.
    if (selected.length) {
      this.reasonsChanged(selected);
      this.report('applied');
      return;
    }

    this.expandedCategoryId = category?.id ?? null;

    // `!= null` and not truthiness: the id comes from the catalogue, and a
    // category with id 0 would otherwise report as if it had not resolved.
    if (this.expandedCategoryId == null) {
      // The category named by the offer is not in the catalogue -- the rename.
      this.report('unresolved');
      return;
    }

    // The category landed. Whether a named reason did is the other axis, and
    // reaching here with reasons offered means none of them is in the category
    // any more: the same rename, one level down. Reported apart from
    // `unresolved` so the category-level monitor stays precise -- and apart
    // from `applied`, which is what this used to say while the operator was
    // looking at a list with nothing marked on it.
    this.report(reasonIds.length ? 'reason-unresolved' : 'applied');
  }

  /**
   * Once per dialog. `applyPreselection` is reached twice for a preselection
   * that resolves to a category and nothing else -- `reasons.value` stays empty,
   * so the guard at the top does not stop the second pass -- and a funnel
   * counting the same offer twice is worse than one that misses it.
   */
  private report(outcome: PreselectionOutcome): void {
    if (this.preselectionReported) return;

    this.preselectionReported = true;
    this.preselectionApplied.emit(outcome);
  }

  private categoryByName(name: string): Category | undefined {
    const wanted = name.trim().toLocaleLowerCase();

    return this.categories.find(
      ({ name: label }: Category) => label.trim().toLocaleLowerCase() === wanted
    );
  }

  private buildReasons(
    items: ResponseReason[] | ResponseSubReason[],
    isChild?: boolean
  ): Category[] {
    const categories: Category[] = [];

    items.forEach((reason: ResponseReason | ResponseSubReason) => {
      const children = (reason as ResponseReason)?.reasons,
        category: Category = {
          id: reason.id,
          name: reason.label,
        };

      if (children?.length) {
        categories.push({
          ...category,
          children: this.buildReasons(children, true),
        });
      } else if (isChild) {
        categories.push(category);
      }
    });

    return categories;
  }

  private enableMessage(): void {
    if (this.reasons.value?.length) {
      this.message.enable();
    }
  }
}
